import { Button, DatePicker, Input, Select, Tooltip } from "antd";
import { TableProps } from "antd/es/table";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import FilterIcon from "../../../assets/icons/FilterIcon";
import ReloadIcon from "../../../assets/icons/ReloadIcon";
import RemarkIcon from "../../../assets/icons/RemarkIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SettingIcon from "../../../assets/icons/SettingIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import UploadIcon from "../../../assets/icons/UploadIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import AttendanceLogModal from "../../../components/Attendance/AttendanceLogModal";
import FilterModal from "../../../components/Attendance/DailyAttendance/FilterModal";
import ImportModal from "../../../components/Attendance/DailyAttendance/ImportModal";
import RequestDateWithOutDateModal from "../../../components/Report/RequestDateWithOutDateModal";
import CustomTable from "../../../components/ui/CustomTable";
import IconButton from "../../../components/ui/IconButton";
import colors from "../../../constants/colors";
import {
  dbDateFormat,
  dbTimeFormat,
  employeeSearchTypeList,
} from "../../../constants/constants";
import { useGetDailyAttendanceReportQuery } from "../../../features/attendanceReport/attendanceReportApi";
import { useDailyAttendanceReportMutation } from "../../../features/report/reportApi";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { convertMinutesToHoursWithText } from "../../../lib/time";
import {
  GetDailyAttendanceReportParams,
  GetDailyAttendanceReportResponseType,
} from "../../../types/attendance";
import { countParPageSerialNumber } from "../../../utils/number";
import { replaceAllWithUndefined } from "../../../utils/obj";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  selectSearchOption,
  skipData,
  underscoreToCapitalizeString,
} from "../../../utils/utils";

dayjs.extend(customParseFormat);

type ModalType = "IMPORT" | "FILTER" | "REPORT_REQUEST" | "LOG";
export type DailyAttendanceFilterType = Omit<
  GetDailyAttendanceReportParams,
  "limit" | "skip" | "search_keyword"
>;

const searchTypeSelect = employeeSearchTypeList?.map((item) => ({
  label: underscoreToCapitalizeString(item),
  value: item,
}));

export default function DailyAttendance() {
  const { keyword, handleInputChange } = useDebounce();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { query } = useQueryParams();
  const { page, limit, handlePagination, handleLimit, resetPagination } =
    useTable<DailyAttendanceFilterType>();
  const [filter, setFilter] = useState<DailyAttendanceFilterType>({
    date: dayjs().format(dbDateFormat),
    status: (query.status as DailyAttendanceFilterType["status"]) || "present",
    checkin_status:
      (query.checkin_status as DailyAttendanceFilterType["checkin_status"]) ||
      undefined,
    search_type: "name",
    tracking:
      (query.tracking as DailyAttendanceFilterType["tracking"]) || undefined,
  });
  const { open, handleClose, handleOpen } = useModal<undefined, ModalType>();

  const { isLoading, isError, error, data, isFetching } =
    useGetDailyAttendanceReportQuery({
      params: {
        ...replaceAllWithUndefined(filter),
        limit,
        skip: skipData(page, limit),
        search_keyword: keyword,
        manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
      },
    });
  const [
    dailyAttendanceReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useDailyAttendanceReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get daily attendance report fail");

  const handleFilter = <K extends keyof DailyAttendanceFilterType>(
    filterType: K,
    value: DailyAttendanceFilterType[K]
  ) => {
    setFilter((prev) => ({
      ...prev,
      [filterType]: value,
    }));

    resetPagination();
  };

  const columns: TableProps<
    GetDailyAttendanceReportResponseType["data"]["attendanceList"][0]
  >["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: () => <span className="text-nowrap">Employee Id</span>,
      dataIndex: "employee_id",
      width: 140,
      render: (val) => val ?? "n/a",
    },
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      width: 310,
      render: (val, record) =>
        val ? (
          <div className="min-w-32 flex items-center gap-1.5">
            {record?.pro_pic ? (
              <img
                className="w-8 h-8 rounded-full"
                src={record?.pro_pic}
                alt=""
              />
            ) : null}
            {val}
          </div>
        ) : (
          "n/a"
        ),
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Shift Name",
      // dataIndex: "shift_name",
      // minWidth: 100,
      render: (val) => {
        const value = underscoreToCapitalizeString(val?.shift_name);
        return (
          <span className="text-nowrap">
            {val?.shift_name ? (
              val?.shift_color ? (
                <span
                  className="text-white px-2 py-1 rounded"
                  style={{
                    backgroundColor: val?.shift_color,
                  }}
                >
                  {value}
                </span>
              ) : (
                value
              )
            ) : (
              "n/a"
            )}
          </span>
        );
      },
    },
    {
      title: "Device ID",
      // minWidth: 100,
      dataIndex: "check_in_log",
      render: (val) =>
        val?.device_id === "manual_update"
          ? underscoreToCapitalizeString(val?.device_id)
          : val?.device_id || "n/a",
    },
    {
      title: "Status",
      // minWidth: 100,
      render: (_, record) => {
        const item = {
          label: "absent",
          text: "",
          className: "bg-extra-gray",
        };

        if (record?.is_leave) {
          item.label = "leave";
          item.text = record?.leave_type_title;
          item.className = "bg-[#2d3643]";
        } else if (record?.is_holiday) {
          item.label = "holiday";
        } else if (record?.is_weekend) {
          item.label = "weekend";
        } else if (record?.is_present) {
          item.label = "present";
        }

        return (
          <>
            {item.text ? (
              <Tooltip title={item.text}>
                <span className={`status text-white ${item.className}`}>
                  {capitalize(item.label)}
                </span>
              </Tooltip>
            ) : (
              <span className={`status text-white ${item.className}`}>
                {capitalize(item.label)}
              </span>
            )}
          </>
        );
      },
    },
    {
      title: "Check In",
      // dataIndex: "check_in",
      width: 150,
      sorter: (a, b) => {
        const timeA = dayjs(a?.check_in, dbTimeFormat).isValid()
          ? dayjs(a?.check_in, dbTimeFormat)
          : dayjs("00:00:00", dbTimeFormat);
        const timeB = dayjs(b?.check_in, dbTimeFormat).isValid()
          ? dayjs(b?.check_in, dbTimeFormat)
          : dayjs("00:00:00", dbTimeFormat);

        return timeA.diff(timeB);
      },
      sortIcon: () => <SortIcon />,
      render: (val) => {
        return val?.check_in ? (
          val?.is_attendance_reconciled ? (
            <Tooltip
              placement="top"
              title={`Reconciliation by super admin (${val?.check_in_log?.updated_by_name})`}
            >
              <span
                className="min-w-[110px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white cursor-pointer"
                style={{
                  backgroundColor:
                    val?.check_in_log?.status === "on_time" ||
                    val?.check_in_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                <ReloadIcon />
                {dayjs(val?.check_in, dbTimeFormat).format("h:mm:ss A")}
              </span>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-2">
              <span
                className="min-w-[100px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white"
                style={{
                  backgroundColor:
                    val?.check_in_log?.status === "on_time" ||
                    val?.check_in_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                {dayjs(val?.check_in, dbTimeFormat).format("h:mm:ss A")}
              </span>
              {!!val?.check_in_log?.is_auto_wifi_attendance ? (
                <span className="tooltip-box">A</span>
              ) : val?.check_in_log?.is_in_wifi ? (
                <Tooltip
                  placement="top"
                  title={val?.check_in_log?.business_office?.name}
                >
                  <span className="tooltip-box">W</span>
                </Tooltip>
              ) : val?.check_in_log?.is_remote ? (
                <Tooltip
                  placement="top"
                  title={val?.check_in_log?.location?.address}
                >
                  <span className="tooltip-box">H</span>
                </Tooltip>
              ) : val?.check_in_log?.is_geo_location ? (
                <Tooltip
                  placement="top"
                  title={val?.check_in_log?.business_office?.name}
                >
                  <span className="tooltip-box">G</span>
                </Tooltip>
              ) : null}
              {val?.check_in_log?.visit_id ? (
                <span className="tooltip-box">V</span>
              ) : null}
              {val?.check_in_log?.note ? (
                <Tooltip placement="top" title={val?.check_in_log?.note}>
                  <div>
                    <RemarkIcon className="cursor-pointer" />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          )
        ) : (
          "n/a"
        );
      },
    },
    {
      title: "Check Out",
      // dataIndex: "check_out",
      width: 150,
      sorter: (a, b) => {
        const timeA = dayjs(a?.check_out, dbTimeFormat).isValid()
          ? dayjs(a?.check_out, dbTimeFormat)
          : dayjs("00:00:00", dbTimeFormat);
        const timeB = dayjs(b?.check_out, dbTimeFormat).isValid()
          ? dayjs(b?.check_out, dbTimeFormat)
          : dayjs("00:00:00", dbTimeFormat);

        return timeA.diff(timeB);
      },
      sortIcon: () => <SortIcon />,
      render: (val) =>
        val?.check_out ? (
          val?.is_attendance_reconciled ? (
            <Tooltip
              placement="top"
              title={`Reconciliation by super admin (${val?.check_out_log?.updated_by_name})`}
            >
              <span
                className="min-w-[110px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white cursor-pointer"
                style={{
                  backgroundColor:
                    val?.check_out_log?.status === "on_time" ||
                    val?.check_out_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                <ReloadIcon />
                {dayjs(val?.check_out, dbTimeFormat).format("h:mm:ss A")}
              </span>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-2">
              <span
                className="min-w-[100px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white"
                style={{
                  backgroundColor:
                    val?.check_out_log?.status === "on_time" ||
                    val?.check_out_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                {dayjs(val?.check_out, dbTimeFormat).format("h:mm:ss A")}
              </span>

              {!!val?.check_out_log?.is_auto_wifi_attendance ? (
                <span className="tooltip-box">A</span>
              ) : val?.check_out_log?.is_in_wifi ? (
                <Tooltip
                  placement="top"
                  title={val?.check_out_log?.business_office?.name}
                >
                  <span className="tooltip-box">W</span>
                </Tooltip>
              ) : val?.check_out_log?.is_remote ? (
                <Tooltip
                  placement="top"
                  title={val?.check_out_log?.location?.address}
                >
                  <span className="tooltip-box">H</span>
                </Tooltip>
              ) : val?.check_out_log?.is_geo_location ? (
                <Tooltip
                  placement="top"
                  title={val?.check_out_log?.business_office?.name}
                >
                  <span className="tooltip-box">G</span>
                </Tooltip>
              ) : null}
              {val?.check_out_log?.note ? (
                <Tooltip placement="top" title={val?.check_out_log?.note}>
                  <div>
                    <RemarkIcon className="cursor-pointer" />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          )
        ) : (
          "n/a"
        ),
    },
    {
      title: "Log",
      dataIndex: "check_in_log",
      render: (val) => {
        return val?.attendance_id ? (
          <Button
            className="download-button !p-0 !h-fit !border-none !shadow-none"
            size="small"
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({ type: "LOG", data: val?.attendance_id })
            }
          >
            View
          </Button>
        ) : (
          "-"
        );
      },
    },
    {
      title: () => (
        <span className="min-w-[120px] inline-block">Net Office Hours</span>
      ),
      // title: "Hours",
      // dataIndex: "hours",
      dataIndex: "staying_time_in_minutes",
      render: (val: number) => (
        <span className="text-nowrap">
          {convertMinutesToHoursWithText(val || 0)}
        </span>
      ),
      sorter: (a, b) => a?.staying_time_in_minutes - b?.staying_time_in_minutes,
      sortIcon: () => <SortIcon />,
    },
    {
      title: () => (
        <span className="min-w-[140px] inline-block">Total Working Hours</span>
      ),
      render: (val: any) => {
        const calculateWorkingMinutes = (el: any): number => {
          if (!el?.check_in || !el?.check_out || !el?.date) {
            return 0;
          }

          const baseDate = moment(el.date);

          if (!baseDate.isValid()) {
            return 0;
          }

          const checkInTime = moment(el.check_in, "HH:mm:ss");
          const checkOutTime = moment(el.check_out, "HH:mm:ss");

          if (!checkInTime.isValid() || !checkOutTime.isValid()) {
            return 0;
          }

          const checkIn = moment.tz(
            `${baseDate.format("YYYY-MM-DD")} ${el.check_in}`,
            "YYYY-MM-DD HH:mm:ss",
            "Asia/Dhaka"
          );

          let checkOutDate = baseDate;
          if (checkInTime.isAfter(checkOutTime, "second")) {
            checkOutDate = checkOutDate.add(1, "day");
          }

          const checkOut = moment.tz(
            `${checkOutDate.format("YYYY-MM-DD")} ${el.check_out}`,
            "YYYY-MM-DD HH:mm:ss",
            "Asia/Dhaka"
          );

          if (!checkIn.isValid() || !checkOut.isValid()) {
            return 0;
          }

          const diffMinutes = checkOut.diff(checkIn, "minutes");

          return diffMinutes > 0 ? diffMinutes : 0;
        };

        return (
          <span className="text-nowrap">
            {convertMinutesToHoursWithText(calculateWorkingMinutes(val))}
          </span>
        );
      },
    },
    {
      title: "Overtime",
      // dataIndex: "overtime_hours",
      dataIndex: "overtime_in_minutes",
      render: (val) => (
        <span className="text-nowrap">
          {convertMinutesToHoursWithText(val || 0)}
        </span>
      ),
      sorter: (a, b) => a?.overtime_in_minutes - b?.overtime_in_minutes,
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Remarks",
      // align: "center",
      dataIndex: "remarks",
      render: (val) => (
        <Tooltip title={val || "-"}>
          <div className="w-[200px] text-ellipsis overflow-hidden whitespace-nowrap">
            {val || "-"}
          </div>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    if (keyword) resetPagination();
  }, [keyword, resetPagination]);

  return (
    <SidebarLayout title="Daily Attendance">
      {open?.type === "LOG" && (
        <AttendanceLogModal
          {...open}
          type={open?.type}
          handleClose={handleClose}
        />
      )}

      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalAttendanceList}
        dataSource={addKeyInArray(data?.data.attendanceList)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Select
              showSearch
              placeholder="Search Type"
              value={filter?.search_type}
              options={searchTypeSelect}
              onChange={(val) => handleFilter("search_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[120px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Input
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                filter?.search_type || "name",
                true
              )}`}
              style={{ maxWidth: 180 }}
              onChange={(val) => {
                handleInputChange(val);
              }}
            />
            {/* <Input
              suffix={<SearchIcon />}
              placeholder="Search by name"
              // id
              onChange={handleInputChange}
            /> */}
            <IconButton
              onClick={() =>
                handleOpen({
                  type: "FILTER",
                })
              }
              className="normal-button"
              nextIcon={<FilterIcon />}
            >
              Filter
            </IconButton>
            <DatePicker
              format="DD MMM, YYYY"
              style={{ width: 140 }}
              suffixIcon={<DateIcon />}
              className="clear-button-hide"
              value={filter?.date ? dayjs(filter?.date) : null}
              onChange={(date) => {
                const formattedDate = date?.format(dbDateFormat);
                handleFilter("date", formattedDate);
              }}
            />
            <Button
              onClick={() =>
                handleOpen({
                  type: "IMPORT",
                })
              }
              className="upload-button"
              icon={<UploadIcon />}
            >
              Import
            </Button>
            {/* test comment */}
            {/* <Button
              className="download-button"
              icon={<DownloadIcon />}
              loading={excelLoading}
              onClick={handleDownload}
            >
              Export
            </Button> */}
            <Button
              onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
              className="download-button"
              icon={<DownloadIcon />}
            >
              Report Requests
            </Button>
            <Link to="/admin/settings/office-settings/attendance">
              <Button
                type="primary"
                className="middle-button"
                icon={<SettingIcon />}
              />
            </Link>
          </>
        }
      />

      {open.type === "IMPORT" && (
        <ImportModal open={open.open} handleClose={handleClose} />
      )}
      {open.type === "FILTER" && (
        <FilterModal
          open={open.open}
          handleClose={handleClose}
          filter={filter}
          setFilter={(values) => {
            setFilter(values);
            resetPagination();
          }}
        />
      )}

      {open.type === "REPORT_REQUEST" && (
        <RequestDateWithOutDateModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          detailsMessage={`You are requesting a Report Export for ${dayjs(
            filter?.date,
            dbDateFormat
          ).format("DD/MM/YYYY")}`}
          columnList={columnList}
          handleSubmit={(selected) => {
            dailyAttendanceReport({
              body: {
                name: "Daily Attendance",
                column_list: selected,
              },
              params: { ...filter, skip: 0, limit: 0 },
            });
          }}
        />
      )}
    </SidebarLayout>
  );
}

const columnList = [
  "date",
  "employee_id",
  "employee_name",
  "shift_name",
  "department",
  "address",
  "status",
  "check_in",
  "check_in_log_status",
  "check_in_location",
  "check_in_address",
  "check_out",
  "check_out_log_status",
  "check_out_location",
  "check_out_address",
  "total_hours",
  "overtime",
  "late_check_in_note",
  "left_early_note",
  "attendance_reconciliation",
];
